import { useEffect, useMemo, useState } from 'react'
import { Grid, Checkbox, Tab } from 'semantic-ui-react'
import { connect } from 'react-redux'
import clientActions from '../../store/actions/clients'
import { useTranslation } from 'react-i18next'

const normalizePermissions = (permissions) => {
    return Array.from(
        new Set(
            (Array.isArray(permissions) ? permissions : []).filter(
                (permission) => typeof permission === 'string' && permission.length > 0
            )
        )
    )
}

const collectNodeUrls = (node) => {
    if (!node || typeof node !== 'object') {
        return []
    }

    const currentNodeUrl = typeof node.url === 'string' ? [node.url] : []
    const children = Array.isArray(node.children) ? node.children : []

    return currentNodeUrl.concat(children.flatMap((child) => collectNodeUrls(child)))
}

const collectNodesUrls = (nodes) => {
    return Array.from(
        new Set(
            (Array.isArray(nodes) ? nodes : []).flatMap((node) => collectNodeUrls(node))
        )
    )
}

const applyUrlSelection = (permissions, urls, shouldSelect) => {
    const nextPermissions = new Set(normalizePermissions(permissions))

    urls.forEach((url) => {
        if (shouldSelect) {
            nextPermissions.add(url)
            return
        }
        nextPermissions.delete(url)
    })

    return Array.from(nextPermissions)
}

const ClientContactWebPermissions = ({ availableModules, currentPermissions, onChange = () => { } }) => {
    const { t } = useTranslation()
    const [localCurrentPermissions, setLocalCurrentPermissions] = useState(normalizePermissions(currentPermissions))
    const localCurrentPermissionsSet = useMemo(
        () => new Set(localCurrentPermissions),
        [localCurrentPermissions]
    )

    useEffect(() => {
        setLocalCurrentPermissions(normalizePermissions(currentPermissions))
    }, [currentPermissions])

    const updatePermissions = (updater) => {
        setLocalCurrentPermissions((previousPermissions) => {
            const nextPermissions = normalizePermissions(
                typeof updater === 'function'
                    ? updater(previousPermissions)
                    : updater
            )

            onChange(nextPermissions)
            return nextPermissions
        })
    }

    const toggleAllModule = (value, moduleIndex) => {
        const currentModule = availableModules[moduleIndex]
        if (!currentModule) {
            return
        }

        const moduleName = Object.keys(currentModule)[0]
        const submodules = currentModule[moduleName]?.routes
        const allSubmodules = collectNodesUrls(submodules)

        if (allSubmodules.length === 0) {
            return
        }

        updatePermissions((prevPermissions) =>
            applyUrlSelection(prevPermissions, allSubmodules, !!value)
        )
    }

    const toggleNode = (node, value) => {
        const nodeUrls = collectNodeUrls(node)

        if (nodeUrls.length === 0) {
            return
        }

        updatePermissions((prevPermissions) =>
            applyUrlSelection(prevPermissions, nodeUrls, !!value)
        )
    }

    const renderRouteNode = (route, level = 0, keyPrefix = '') => {
        const nodeUrls = collectNodeUrls(route)

        if (nodeUrls.length === 0) {
            return null
        }

        const isChecked = nodeUrls.every((url) => localCurrentPermissionsSet.has(url))
        const isIndeterminate =
            !isChecked && nodeUrls.some((url) => localCurrentPermissionsSet.has(url))
        const children = Array.isArray(route?.children) ? route.children : []
        const label = route?.name || route?.title || route?.url

        return (
            <div
                key={`${keyPrefix}-${route?.url || route?.name || level}`}
                style={{ marginTop: 8, marginLeft: level * 18 }}
            >
                <Checkbox
                    label={label}
                    checked={isChecked}
                    indeterminate={isIndeterminate}
                    onClick={(e, data) => {
                        const nextValue =
                            typeof data?.checked === 'boolean' ? data.checked : !isChecked
                        toggleNode(route, nextValue)
                    }}
                />

                {children.length > 0 && (
                    <div style={{ marginLeft: 18 }}>
                        {children.map((child, index) =>
                            renderRouteNode(child, level + 1, `${keyPrefix}-${index}`)
                        )}
                    </div>
                )}
            </div>
        )
    }

    return (
        <Tab.Pane attached={false}>
            <Grid>
                {availableModules.map((permission, moduleIndex) => {
                    const moduleName = Object.keys(permission)[0]
                    const submodules = permission[moduleName]?.routes
                    const moduleUrls = collectNodesUrls(submodules)
                    const moduleChecked =
                        moduleUrls.length > 0 &&
                        moduleUrls.every((url) => localCurrentPermissionsSet.has(url))
                    const moduleIndeterminate =
                        !moduleChecked &&
                        moduleUrls.some((url) => localCurrentPermissionsSet.has(url))

                    return (
                        <Grid.Row key={moduleName}>
                            <Grid.Column width={16} style={{ marginBottom: 10 }}>
                                <Checkbox
                                    label={moduleName}
                                    style={{ fontWeight: 'bold' }}
                                    checked={moduleChecked}
                                    indeterminate={moduleIndeterminate}
                                    onClick={(e, data) => {
                                        const nextValue =
                                            typeof data?.checked === 'boolean'
                                                ? data.checked
                                                : !moduleChecked
                                        toggleAllModule(nextValue, moduleIndex)
                                    }}
                                />

                                <div style={{ marginLeft: 18, marginTop: 6 }}>
                                    {(Array.isArray(submodules) ? submodules : []).map(
                                        (submodule, submoduleIndex) =>
                                            renderRouteNode(
                                                submodule,
                                                0,
                                                `${moduleName}-${submoduleIndex}`
                                            )
                                    )}
                                </div>
                            </Grid.Column>
                        </Grid.Row>
                    )
                })}
            </Grid>
        </Tab.Pane>
    )
}

const mapDispatchToProps = (dispatch) => ({
    getClientModules: () => dispatch(clientActions.getClientModules()),
})

export default connect(
    null,
    mapDispatchToProps
)(ClientContactWebPermissions)
